import { h, Component, State, Listen } from '@stencil/core';
import { transformApiTablaDefToEditableGrid } from '../../helpers/edGridApiResponseTransformer';
import { type UrlParamsForApiTablaDef, type EditableGridTransformed } from '../../types/TablaDefTypes';
// import data from '../../ejemplo-data/data.json';
import { showModal } from '../../components/base/generic-modal';
import { parseUrlParamsForAPiTablaDef } from '../../helpers/parseUrlParamsApiTablasDef';
import { getTablaDefGetUrl, getTablaDefSaveUrl } from '../../helpers/getApis';
import { checkTablaDefDataFromApi } from '../../helpers/checkTablaDefDataFromApi';
import { transformDirtyChangesToApiFormat } from '../../helpers/edGridApiSaveTransformer';

@Component({
  tag: 'tabladef-home',
  styleUrl: 'tabladef-home.css',
  shadow: true,
})
export class TabladefHome {
  @State() isLoading: boolean = true;
  @State() tableCellChanges: number = 0;

  @Listen('cell-changed')
  handleCellChanged({ detail: changesAmmount = 0 }: CustomEvent<number>) {
    this.tableCellChanges = changesAmmount;
  }

  @Listen('save-btn-clicked')
  handleSaveBtnClicked() {
    this.handleSave();
  }

  @Listen('cancel-btn-clicked')
  handleCancelBtnClicked() {
    this.handleCancel();
  }

  @Listen('exit-btn-clicked')
  handleExitBtnClicked() {
    this.handleExit();
  }

  private async handleSave() {
    this.saveData();
  }

  private async handleCancel() {
    const modalResult = await this.showModalCancelarCambios();

    if (modalResult.type === 'action') {
      if (modalResult.value === 'discard') {
        this.editableGridElement.clearChangesNoSave();
      }
    }
  }

  private async handleExit() {
    if (this.tableCellChanges === 0) {
      window.close();
      return;
    }

    const modalResult = await this.showModalSalirCambios();

    if (modalResult.type === 'action') {
      if (modalResult.value === 'save') {
        this.saveData();
      }
      if (modalResult.value === 'exit') {
        window.close();
      }
    }
  }

  private hasError?: string;
  private gridData?: EditableGridTransformed;

  private editableGridElement!: HTMLEditableGridElement;

  private urlSearchParams!: UrlParamsForApiTablaDef;

  componentWillLoad() {
    try {
      const params = parseUrlParamsForAPiTablaDef(window.location.search);
      this.urlSearchParams = params;
    } catch (error) {
      console.error('Error parseando parametros: ', error);
      this.hasError = 'Error: faltan los parametros o son incorrectos.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.loadData()
      .then((data) => {
        this.gridData = data;
        this.editableGridElement.loadGridData(data);
      })
      .catch((error) => {
        this.hasError = error.message;
        console.error('Error al cargar los datos:', error);
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  private async loadData(): Promise<EditableGridTransformed> {
    this.hasError = undefined;
    const url = new URL(getTablaDefGetUrl());

    Object.entries(this.urlSearchParams).forEach(([key, value]) => {
      url.searchParams.append(key, String(value));
    });

    return (
      fetch(url.toString(), {
        method: 'GET',
        headers: {
          Accept: 'application/json',
        },
      })
        .then((res) => res.json())
        .then((data) => checkTablaDefDataFromApi(data))
        // .then(() => data)
        .then((data) => transformApiTablaDefToEditableGrid(data))
        .catch(() => {
          throw new Error('Error: no se puede obtener datos');
        })
    );
  }

  private async saveData() {
    this.isLoading = true;
    this.hasError = undefined;

    this.editableGridElement
      .getAllGridChanges()
      .then((changes) => transformDirtyChangesToApiFormat(changes, this.gridData!.columns))
      .then((apiData) => {
        const payload = { ...this.urlSearchParams, SDT_Def_demple: apiData };
        console.log('Cambios a guardar:', payload);

        return fetch(getTablaDefSaveUrl(), {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }).then((res) => res.json());
      })
      .then(() => {
        this.editableGridElement.clearDirtyChanges();
      })
      .catch((error) => {
        this.hasError = 'Error al guardar datos.';
        console.error('Error al guardar datos:', error);
      })
      .finally(() => {
        setTimeout(() => {
          this.isLoading = false;
        }, 2000);
      });
  }

  private getHeaderBtnState() {
    return {
      loadingData: this.isLoading,
      changes: this.tableCellChanges,
    };
  }

  render() {
    return (
      <div id='tabladefHome'>
        <header>
          <div class='header-container'>
            <tabladef-header btnState={this.getHeaderBtnState()} dataTabla={this.urlSearchParams} />
            <div class='header-messages'>
              {this.isLoading ? <dots-loader /> : null}
              {this.hasError ? <span>{this.hasError}</span> : null}
            </div>
          </div>
        </header>

        <main>
          <editable-grid
            dataState={this.hasError ? 'error' : this.isLoading ? 'loading' : 'loaded'}
            ref={(el) => {
              this.editableGridElement = el!;
            }}
          />
        </main>
      </div>
    );
  }

  /**
   * @deprecated La accion guardar ya no muestra el modal de confirmacion,
   * guarda directamente.
   * @returns void
   */
  private showModalConfirmarGuardado() {
    return showModal('modal-confirmar-guardado');
  }

  private showModalCancelarCambios() {
    return showModal('modal-cancelar-cambios');
  }

  private showModalSalirCambios() {
    return showModal('modal-salir-cambios');
  }
}
